import { AbstractDialog } from "../abstract_dialog.js";
import { ConfigSyncService } from "../../../config_sync/config_sync_service.js";
import { DialogManager } from "../dialog_manager.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { DialogId } from "../dialog_id.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { performLogin } from "../../../api/perform_login.js";
import { clearFacebookAvatarCache } from "../../../common/facebook_avatar_loader.js";
import { FacebookSignInPlugin } from "../../../plugins/facebook_sign_in_plugin.js";
import { GoogleSignInPlugin } from "../../../plugins/play_games_plugin.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { applePlugin } from "../../../api/account_link_service.js";
import { AuthMessageDialog } from "./auth_message_dialog.js";
import { NetworkError } from "../../../api/http_client.js";
import { OfflineJournal } from "../../../offline/offline_journal.js";
import { DisconnectAccountNotification } from "../disconnect_account_notification.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { Vector2 } from "../../../common/vector2.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";

export class ExitFromGameAccountDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.EXIT_FROM_GAME_ACCOUNT_DIALOG;

  private close_btn!: UIImage;
  private is_exiting = false;

  constructor()
  {
    const design_body_w = 910;
    const design_body_h = 1034;

    const body_w = LayoutScaler.widthFromDesignAdaptive( design_body_w );
    const body_h = LayoutScaler.heightFromDesign( design_body_h );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;
    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createTopBgPart();
    this.createWarningIcon();
    this.createTitle();
    this.createWarningText();
    this.createActionButtons();
    this.createCloseButton();
  }

  private createActionButtons(): void
  {
    const design_btn_w = 380;
    const design_btn_h = 112;
    const design_gap = 24;
    const design_btn_y = 861;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );
    const gap   = LayoutScaler.widthFromDesign( design_gap );
    const total_w = btn_w * 2 + gap;
    const start_x = this.rect.getW2() - total_w / 2;
    const btn_y = LayoutScaler.yFromDesign( design_btn_y );

    const cancel_btn = new RoundedRectangle(
      this.ctx,
      start_x,
      btn_y,
      btn_w,
      btn_h,
      btn_h / 2,
      "#10B2E2",
      undefined,
      true
    );
    cancel_btn.addChild( this.actionButtonText( "CANCEL", btn_w, btn_h ), true );
    cancel_btn.setOnClick( () => { this.close(); } );
    this.addChild( cancel_btn );

    const exit_btn = new RoundedRectangle(
      this.ctx,
      start_x + btn_w + gap,
      btn_y,
      btn_w,
      btn_h,
      btn_h / 2,
      "#F2A618",
      undefined,
      true
    );
    exit_btn.addChild( this.actionButtonText( "EXIT_FROM_GAME_ACCOUNT_DIALOG.EXIT_BUTTON", btn_w, btn_h ), true );
    exit_btn.setOnClick( () => { void this.exitAccount(); } );
    this.addChild( exit_btn );
  }

  private actionButtonText( localization_key: string, btn_w: number, btn_h: number ): UITextElement
  {
    const design_font_size = 47;

    return new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        using_y_offset: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );
  }

  private warningTextOptions()
  {
    const design_font_size = 50;
    const design_outline_w = 10;

    return {
      text_color: ColorScheme.white,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
      font_weight: 700,
      auto_wrap: true,
      outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
      outline_color: ColorScheme.settings_dialog_title_outline,
    };
  }

  private createWarningText(): void
  {
    const design_w = 760;
    const design_h = 546;
    const design_y = 300;

    const label_w = LayoutScaler.widthFromDesign( design_w );
    const label_h = LayoutScaler.heightFromDesign( design_h );
    const label_x = this.rect.getW2() - label_w / 2;
    const label_y = LayoutScaler.yFromDesign( design_y );

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "EXIT_FROM_GAME_ACCOUNT_DIALOG.WARNING",
        ...this.warningTextOptions(),
      }
    );

    this.addChild( label );
  }

  private createTitle(): void
  {
    const design_h = 188;
    const design_y = 117;
    const design_font_size = 70;
    const design_outline_w = 20;

    const label_w = this.rect.getW() * 0.8;
    const label_h = LayoutScaler.heightFromDesign( design_h );
    const label_x = this.rect.getW2() - label_w / 2;
    const label_y = LayoutScaler.yFromDesign( design_y );

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "EXIT_FROM_GAME_ACCOUNT_DIALOG.TITLE",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        auto_wrap: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.booster_dialog_title_outline,
      }
    );

    this.addChild( label );
  }

  // Decorative arch poking above the top edge — same idea as ProfileDialog's
  // avatar circle, but using the bg_part.png "peek" graphic instead.
  private createTopBgPart(): void
  {
    const design_w = 237;
    const design_h = 128;
    const design_y = -80;

    const part_w = LayoutScaler.widthFromDesign( design_w );
    const part_h = LayoutScaler.heightFromDesign( design_h );
    const part_x = this.rect.getW2() - part_w / 2;
    const part_y = LayoutScaler.yFromDesign( design_y );

    const bg_part = new UIImage(
      this.ctx,
      part_x,
      part_y,
      part_w,
      part_h,
      ImgID.UI_CapsuleTopBgPart,
      {},
      false
    );
    this.addChild( bg_part );
  }

  private createWarningIcon(): void
  {
    const design_w = 160;
    const design_h = 141;
    const design_y = -29;

    const icon_w = LayoutScaler.widthFromDesign( design_w );
    const icon_h = LayoutScaler.heightFromDesign( design_h );
    const icon_x = this.rect.getW2() - icon_w / 2;
    const icon_y = LayoutScaler.yFromDesign( design_y );

    const warning_icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_w,
      icon_h,
      ImgID.UI_WarningIcon,
      {},
      false
    );
    this.addChild( warning_icon );
  }

  private async exitAccount(): Promise<void>
  {
    if ( this.is_exiting ) return;
    this.is_exiting = true;

    OfflineJournal.clear();
    const logged_in = await performLogin( { force_guest: true } );
    if ( !logged_in )
    {
      this.is_exiting = false;
      DialogManager.openDialog( AuthMessageDialog.forAccountError( new NetworkError(), "AUTH_ERROR.GENERIC" ) );
      return;
    }

    await Promise.allSettled( [
      GoogleSignInPlugin.signOut(),
      FacebookSignInPlugin.signOut(),
      applePlugin().signOut(),
    ] );

    localStorage.removeItem( 'web_google_id' );
    localStorage.removeItem( 'web_facebook_id' );
    localStorage.removeItem( 'native_apple_id' );
    localStorage.removeItem( 'analytics_userid' );
    clearFacebookAvatarCache();

    await ConfigSyncService.refreshAll( 'account_switch' );

    DialogManager.closeAllDialogs();
    ScreenManager.showScreen( Screens.MENU );
    DialogManager.openDialog( new DisconnectAccountNotification() );
  }

  private createBackground(): void
  {
    const bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( 0 ),
      LayoutScaler.yFromDesign( 0 ),
      this.rect.getW(),
      this.rect.getH(),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( bg );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 117;
    const design_offset_x = 10;
    const design_offset_y = 100;

    const btn_size = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_x = this.rect.getW() - btn_size + LayoutScaler.widthFromDesign( design_offset_x );
    const btn_y = -LayoutScaler.yFromDesign( design_offset_y );

    this.close_btn = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_size,
      btn_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );

    this.close_btn.setOnClick( () => this.close() );
    this.addChild( this.close_btn );
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public name(): string
  {
    return "ExitFromGameAccountDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }
}
